import { useRef } from 'react';

import { Loading } from '#loading/loading.component.tsx';

import type { TargetedLoaderProps } from './types/targeted-loader.props';

/**
 * A React component that displays a loading indicator targeted to a specific element,
 * with optional control over the visibility of its children during loading.
 *
 * @param children - The content to render inside the loader container.
 * @param hideChildren - If true, hides the children when loading is active. Defaults to true.
 * @param loading - If true, displays the loading indicator.
 * @param ref - Optional ref to the target element for the loading indicator. If not provided, an internal ref is used.
 * @param size - The size of the loading indicator. Defaults to 1.
 * @param type - The type of loading indicator to display. Defaults to 'dual-ring'.
 *
 * @remarks
 * The loading indicator is rendered outside the main content container and is targeted to the referenced element.
 */
export const TargetedLoader = ({
  children,
  hideChildren = true,
  loading,
  ref,
  size = 1,
  type = 'dual-ring',
}: TargetedLoaderProps) => {
  const innerRef = useRef<HTMLDivElement | null>(null);
  const targetRef = ref ?? innerRef;

  return (
    <>
      <div ref={innerRef}>
        {!hideChildren && <>{children}</>}
        {!loading && hideChildren && <>{children}</>}
      </div>
      {loading && (
        <Loading
          size={size}
          targetRef={targetRef}
          type={type}
        />
      )}
    </>
  );
};
